import { useCallback, useEffect, useRef, useState } from 'react';

import {
    interpolateColor,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { VideoItem } from 'services/data-manager/types';

import { COLOR } from 'utils/colors';
import isFunction from 'utils/function';
import { isWeb } from 'utils/responsiveness';

type Params = {
    screenReaderEnabled: boolean;
    subtitlesDialogVisible: boolean | undefined;
    errorExists: boolean;
    readyToPlay: boolean;
    recordingActionsDialogVisible: boolean | undefined;
    videoItem?: VideoItem;
    isAirPlayConnected?: boolean;
    isBingeWatchingVisible?: boolean;
};

const ANIMATION_DURATION = 300;

function useShowHideAnimation({
    screenReaderEnabled,
    subtitlesDialogVisible,
    readyToPlay,
    errorExists,
    recordingActionsDialogVisible,
    videoItem,
    isAirPlayConnected,
    isBingeWatchingVisible,
}: Params) {
    const overlayVisiblityTime = screenReaderEnabled || isWeb ? 15000 : 5000;
    const canShowOverlay =
        readyToPlay &&
        !errorExists &&
        !subtitlesDialogVisible &&
        !recordingActionsDialogVisible &&
        !isAirPlayConnected &&
        !isBingeWatchingVisible;

    const [overlayVisible, setOverlayVisible] = useState(canShowOverlay);
    const sharedOpacityValue = useSharedValue(overlayVisible ? 1 : 0);
    const timeout = useRef<NodeJS.Timeout | undefined>(undefined);

    const animatedStyle = useAnimatedStyle(() => ({
        opacity: sharedOpacityValue.value,
    }));
    const backgroundAnimatedStyle = useAnimatedStyle(() => {
        if (errorExists || !readyToPlay) {
            return { backgroundColor: COLOR.BLACK };
        }

        return {
            backgroundColor: interpolateColor(
                sharedOpacityValue.value,
                [0, 1],
                ['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 0.5)'],
            ),
        };
    }, [errorExists, readyToPlay]);

    const handleHideOverlay = useCallback(() => {
        sharedOpacityValue.value = withTiming(
            0,
            { duration: ANIMATION_DURATION },
            (finished) => {
                if (finished) {
                    runOnJS(setOverlayVisible)(false);
                }
            },
        );
    }, []);

    const handleHideOverlayWithDelay = useCallback(() => {
        if (timeout.current) {
            clearTimeout(timeout.current);
            timeout.current = undefined;
        }

        if (!errorExists) {
            timeout.current = setTimeout(
                handleHideOverlay,
                overlayVisiblityTime,
            );
        }
    }, [errorExists, handleHideOverlay, overlayVisiblityTime]);

    const handleShowOverlay = useCallback(() => {
        setOverlayVisible(true);
        sharedOpacityValue.value = withTiming(
            1,
            { duration: ANIMATION_DURATION },
            (finished) => {
                if (finished) {
                    runOnJS(handleHideOverlayWithDelay)();
                }
            },
        );
    }, [handleHideOverlayWithDelay]);

    const handleShowOverlayConstantly = useCallback(() => {
        if (timeout.current) {
            clearTimeout(timeout.current);
            timeout.current = undefined;
        }

        if (!overlayVisible) {
            setOverlayVisible(true);
            sharedOpacityValue.value = withTiming(1, {
                duration: ANIMATION_DURATION,
            });
        }
    }, [overlayVisible]);

    const handleOverlayPress = useCallback(() => {
        if (!isWeb) {
            if (overlayVisible) {
                handleHideOverlay();
            } else {
                handleShowOverlay();
            }
        }
    }, [overlayVisible, handleHideOverlay, handleShowOverlay]);

    const handleProlongOverlayVisibility = useCallback(
        (action: () => void) => {
            if (isFunction(action)) {
                action();
            }

            handleHideOverlayWithDelay();
        },
        [handleHideOverlayWithDelay],
    );

    useEffect(() => {
        if (canShowOverlay && !overlayVisible) {
            handleShowOverlay();
        }
    }, [canShowOverlay]);

    useEffect(() => {
        if (errorExists) {
            handleShowOverlayConstantly();
        } else {
            handleHideOverlayWithDelay();
        }
    }, [errorExists, handleShowOverlayConstantly, handleHideOverlayWithDelay]);

    useEffect(() => {
        if (subtitlesDialogVisible || recordingActionsDialogVisible) {
            handleShowOverlayConstantly();
        } else {
            handleHideOverlayWithDelay();
        }
    }, [
        subtitlesDialogVisible,
        handleShowOverlayConstantly,
        recordingActionsDialogVisible,
    ]);

    // reset overlay visibility timer on video item change
    useEffect(() => {
        if (overlayVisible && !errorExists) {
            handleHideOverlayWithDelay();
        }
    }, [videoItem]);

    const handleWebMouseMove = useCallback(() => {
        handleShowOverlayConstantly();
        handleHideOverlayWithDelay();
    }, [handleShowOverlayConstantly, handleHideOverlayWithDelay]);

    useEffect(() => {
        if (isAirPlayConnected || isBingeWatchingVisible) {
            handleHideOverlay();
        }
    }, [isAirPlayConnected, isBingeWatchingVisible, handleHideOverlay]);

    return {
        overlayVisible,
        animatedStyle,
        backgroundAnimatedStyle,
        handleOverlayPress,
        handleProlongOverlayVisibility,
        handleShowOverlayConstantly,
        handleWebMouseMove,
    };
}

export default useShowHideAnimation;
